import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { FinanceCostCenterQuery } from 'generated/resources/graphql'
import { CostCenterDetailsSettingsPageQuery } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import SelectField from 'modules/core/components/SelectField'
import Sidepanel from 'modules/core/components/Sidepanel'
import TextField from 'modules/core/components/TextField'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { FormErrors } from 'modules/core/hooks/useForm'
import { useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCostCenters } from 'modules/finance/hooks/useCostCenters'

type CostCenterDetails =
  CostCenterDetailsSettingsPageQuery['finance']['costCentersConnection']['nodes'][0]

interface Props {
  open: boolean
  onClose: () => void
  costCenter:
    | CostCenterDetails // To be deleted after the FF is removed
    | FinanceCostCenterQuery['finance']['costCentersConnection']['nodes'][number]
}

interface EditCostCenterFormValues {
  id: number
  name: string
  code: string
  description: string
  legalEntityName: string
}

const DetailsPanel = ({ costCenter, open, onClose }: Props) => {
  const [submitting, setSubmitting] = useState<boolean>(false)

  const { editCostCenter } = useCostCenters({
    ids: [],
    legalEntityIds: [],
    skipRead: true,
  })

  const form = useForm<EditCostCenterFormValues>({
    defaultValues: {
      id: costCenter.id,
      name: costCenter?.name || '',
      code: costCenter?.code || '',
      description: costCenter?.description || '',
      legalEntityName: costCenter?.legalEntity?.legalName || '',
    },

    validate: (data) => {
      const errors: FormErrors<EditCostCenterFormValues> = {}
      if (!data.name) {
        errors.name = i18n.t('finance.cost_center_detail.sidepanel.mandatory')
      }
      return errors
    },

    onSubmit: async (values) => {
      setSubmitting(true)

      const variables = {
        id: costCenter.id,
        name: values.name,
        code: values.code,
        description: values.description,
      }

      const result = await editCostCenter(variables)

      const handled = handleResult(result, (errors) => {
        form.setErrors({
          name: errors['name'],
          code: errors['code'],
          description: errors['description'],
        })
      })

      if (handled.success) {
        onClose()
        app.setFlash({
          type: 'correct',
          message: i18n.t('finance.cost_center_detail.sidepanel.success'),
        })
      }
      setSubmitting(false)
      return handled
    },
  })

  useUnsafeEffect(() => {
    const values = {
      id: costCenter.id,
      name: costCenter.name,
      description: costCenter.description || '',
      code: costCenter.code || '',
      legalEntityName: costCenter.legalEntity?.legalName || '',
    }

    form.setValues(values)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenter])

  return (
    <Sidepanel
      overlay={false}
      boxPadding={false}
      width="narrow"
      title={i18n.t('finance.cost_center_detail.sidepanel.title')}
      onClose={onClose}
      open={open}
      headerBorder
      closeLabel={i18n.t('common.close')}
      footer={
        form.dirty && (
          <>
            <Button type="secondary" onClick={onClose}>
              {i18n.t('finance.cost_center_detail.sidepanel.cancel')}
            </Button>
            <Button onClick={() => form.handleSubmit()} disabled={submitting}>
              {i18n.t('finance.cost_center_detail.sidepanel.save')}
            </Button>
          </>
        )
      }
    >
      <Box padding="s24" paddingBottom="s12">
        <TextField
          mandatory
          label={i18n.t('finance.cost_center_detail.sidepanel.name')}
          value={form.values.name}
          onChange={(value) => form.setValues({ ...form.values, name: value })}
          error={form.errors.name}
        />
      </Box>
      <Box padding="s24" paddingBottom="s12">
        <TextField
          mandatory
          label={i18n.t('finance.cost_center_detail.sidepanel.code')}
          value={form.values.code}
          onChange={(value) => form.setValues({ ...form.values, code: value })}
          error={form.errors.code}
          secondaryLabel={
            <InformationTooltip
              information={i18n.t('finance.modals.add_cost_center.fields.code.information')}
              icon="question"
            />
          }
        />
      </Box>
      <Box padding="s24" paddingBottom="s12">
        <TextField
          mandatory
          label={i18n.t('finance.cost_center_detail.sidepanel.description')}
          value={form.values.description}
          onChange={(value) => form.setValues({ ...form.values, description: value })}
          error={form.errors.description}
        />
      </Box>
      {costCenter?.legalEntity && (
        <>
          <Box padding="s24" paddingBottom="s12">
            <SelectField
              label={i18n.t('finance.cost_center_detail.sidepanel.legal_entity')}
              value={form.values.legalEntityName}
              options={[
                {
                  label: form.values.legalEntityName,
                  value: form.values.legalEntityName,
                },
              ]}
              readonly
            />
          </Box>
          <Box padding="s24" paddingTop="s12">
            <Box padding="s16" background="grey200" borderRadius={{ all: 'abs008' }}>
              <Text.Normal>
                {i18n.t('finance.cost_center_detail.sidepanel.readonly_legal_entity')}
              </Text.Normal>
            </Box>
          </Box>
        </>
      )}
    </Sidepanel>
  )
}

export default observer(DetailsPanel)
